import { CardType, CardListType, RecCardType } from '@/types/card'
import { groupByModes } from '@/utils/constants'

type Props = {
  cardLists: CardListType[]
  cardMap: Record<string, CardType>
  groupedBy: (typeof groupByModes)[number]['key']
  initCardLists: CardListType[]
  priceSource: keyof CardType['prices']
}

const group = ({ cardLists, cardMap, groupedBy, initCardLists, priceSource }: Props) => {
  const flattened = cardLists
    .filter((cardList) => cardLists.length < 2 || !cardList.header.endsWith('Commanders')) // Remove commanders for non-default groupings
    .flatMap((cardList) => cardList.cardviews)
  switch (groupedBy) {
    case 'default':
      return initCardLists
    case 'date': {
      const reducer = (
        acc: Record<string, { header: string; tag: string; cardviews: RecCardType[] }>,
        card: RecCardType,
      ) => {
        const dateRanges = [
          { label: 'Last 3 Months', date: new Date(new Date().setDate(new Date().getDate() - 90)) },
          { label: 'Last 6 Months', date: new Date(new Date().setDate(new Date().getDate() - 180)) },
          { label: 'Last Year', date: new Date(new Date().setDate(new Date().getDate() - 365)) },
          { label: 'Last 2 Years', date: new Date(new Date().setDate(new Date().getDate() - 730)) },
          { label: 'Since 2020', date: new Date('2020-01-01') },
          { label: 'Since 2010', date: new Date('2010-01-01') },
          { label: 'Since 2000', date: new Date('2000-01-01') },
          { label: 'Earliest', date: new Date('0000-01-01') },
        ]
        const released_at = cardMap[card.cards ? card.cards[0].url : card.sanitized_wo]?.released_at
        if (!released_at) return acc
        const dateRange =
          dateRanges.find(({ date }) => new Date(released_at) > date) || dateRanges[dateRanges.length - 1]
        const i = dateRanges.indexOf(dateRange)
        const { label } = dateRanges[i]
        acc[i] = {
          header: label,
          tag: label,
          cardviews: [...(acc[i]?.cardviews || []), card],
        }
        return acc
      }
      const sorted = Object.values(flattened.reduce(reducer, {})).map(
        ({ cardviews, header, tag }) =>
          ({
            cardviews,
            header,
            tag,
          }) as CardListType,
      )
      return sorted
    }
    case 'mana': {
      const reducer = (acc: Record<string, RecCardType[]>, card: RecCardType) => {
        const cmc =
          card.cards && cardMap[card.cards[0].url].legal_commander
            ? cardMap[card.cards[0].url].cmc + cardMap[card.cards[1].url].cmc
            : cardMap[card.sanitized_wo].cmc
        if (!acc[cmc]) acc[cmc] = []
        acc[cmc].push(card)
        return acc
      }
      const sorted = Object.entries(flattened.reduce(reducer, {})).map(
        ([cmc, grouped]) =>
          ({
            cardviews: grouped,
            header: `${cmc} Mana`,
            tag: `${cmc}`,
          }) as CardListType,
      )
      return sorted
    }
    case 'price': {
      const priceRanges = [
        { label: '< $0.25', min: 0, max: 0.25 },
        { label: '$0.25 - $0.50', min: 0.25, max: 0.5 },
        { label: '$0.50 - $1', min: 0.5, max: 1 },
        { label: '$1 - $2.50', min: 1, max: 2.5 },
        { label: '$2.50 - $5', min: 2.5, max: 5 },
        { label: '$5 - $10', min: 5, max: 10 },
        { label: '$10 - $20', min: 10, max: 20 },
        { label: '$20 - $50', min: 20, max: 50 },
        { label: '$50 - $100', min: 50, max: 100 },
        { label: '$100+', min: 100, max: Infinity },
      ]
      const reducer = (
        acc: Record<string, { header: string; tag: string; cardviews: RecCardType[] }>,
        card: RecCardType,
      ) => {
        const price = cardMap[card.cards ? card.cards[0].url : card.sanitized_wo]?.prices[priceSource]?.price
        if (!price) {
          return {
            ...acc,
            [priceRanges.length]: {
              header: 'Unknown',
              tag: 'Unknown',
              cardviews: [...(acc[priceRanges.length]?.cardviews || []), card],
            },
          }
        }
        const priceRange = priceRanges.find(({ max }) => price < max) || priceRanges[priceRanges.length - 1]
        const i = priceRanges.indexOf(priceRange)
        const { label } = priceRanges[i]
        acc[i] = {
          header: label,
          tag: label,
          cardviews: [...(acc[i]?.cardviews || []), card],
        }
        return acc
      }
      const sorted = Object.values(flattened.reduce(reducer, {})).map(
        // @ts-ignore
        // Can't figure out why the first return in this switch case causes a type error
        ({ cardviews, header, tag }) =>
          ({
            cardviews,
            header,
            tag,
          }) as CardListType,
      ) as CardListType[]
      return sorted
    }
    case 'rarity': {
      const rarities = ['mythic', 'rare', 'uncommon', 'common', 'special']
      const reducer = (
        acc: Record<string, { header: string; tag: string; cardviews: RecCardType[] }>,
        card: RecCardType,
      ) => {
        const rarity = cardMap[card.cards ? card.cards[0].url : card.sanitized_wo]?.rarity
        if (!rarity) return acc
        const i = rarities.indexOf(rarity)
        acc[i] = {
          header: rarity[0].toUpperCase() + rarity.slice(1),
          tag: rarity,
          cardviews: [...(acc[i]?.cardviews || []), card],
        }
        return acc
      }
      const sorted = Object.values(flattened.reduce(reducer, {})).map(
        ({ cardviews, header, tag }) =>
          ({
            cardviews,
            header,
            tag,
          }) as CardListType,
      )
      return sorted
    }
    case 'type': {
      const types = ['Creature', 'Instant', 'Sorcery', 'Artifact', 'Enchantment', 'Battle', 'Planeswalker', 'Land']
      const reducer = (
        acc: Record<string, { header: string; tag: string; cardviews: RecCardType[] }>,
        card: RecCardType,
      ) => {
        const type = cardMap[card.cards ? card.cards[0].url : card.sanitized_wo]?.primary_type
        if (!type) return acc
        const i = types.indexOf(type)
        acc[i] = {
          header: type,
          tag: type,
          cardviews: [...(acc[i]?.cardviews || []), card],
        }
        return acc
      }
      const sorted = Object.values(flattened.reduce(reducer, {})).map(
        ({ cardviews, header, tag }) =>
          ({
            cardviews,
            header,
            tag,
          }) as CardListType,
      )
      return sorted
    }
    case 'ungrouped':
      return [{ cardviews: flattened, header: '', tag: '' }]
    default:
      throw new Error(`Unknown group mode: ${groupedBy}`)
  }
}

export default group
